// The QRISK3 calculation form's state (TGCLOUD-1911): what it starts from, which
// readings the clinician has typed, and what is sent. Pure, so the dialog's
// rules are tested without rendering it.
import { CONDITION_FIELDS } from './qrisk3Text';

const text = (v) => (v === null || v === undefined ? '' : String(v));

// The readings a calculation records as clinical measurements when typed, by
// form field (services/qrisk3 newReadingsOf is the server's twin).
export const READING_FIELDS = [
  'systolicBp',
  'cholesterolHdlRatio',
  'heightCm',
  'weightKg',
  'smoking',
  'townsend'
];

export const CONDITION_KEYS = CONDITION_FIELDS.map((c) => c.key);

/**
 * The form as the prefill fills it. Family history is left unanswered when
 * the pedigree cannot answer it (null), and the clinician must choose.
 * @param {object} prefill  GET /qrisk3/inputs's body
 * @param {string} today    the clinician's calendar day, 'YYYY-MM-DD'
 */
export function formFromPrefill(prefill, today) {
  const p = prefill?.inputs || {};
  const form = {
    sex: p.sex?.value || '',
    age: text(p.age?.value),
    ethnicity: text(p.ethnicity?.value),
    heightCm: text(p.heightCm?.value),
    weightKg: text(p.weightKg?.value),
    bmi: '',
    smoking: text(p.smoking?.value),
    systolicBp: text(p.systolicBp?.value),
    systolicBpSd: text(p.systolicBpSd?.value),
    cholesterolHdlRatio: text(p.cholesterolHdlRatio?.value),
    townsend: text(p.townsend?.value),
    townsendNotKnown: false,
    familyHistoryChd:
      typeof p.familyHistoryChd?.value === 'boolean'
        ? p.familyHistoryChd.value
        : null,
    diabetes: p.diabetes?.value || '',
    confirmedNoCvdNoStatin: false,
    measuredOn: today
  };
  for (const key of CONDITION_KEYS) form[key] = p[key]?.value === true;
  return form;
}

const sameValue = (a, b) => {
  if (a === '' || a === null || a === undefined)
    return b === '' || b === null || b === undefined;
  return Number(a) === Number(b);
};

/** The readings the clinician typed: a value that is not the prefill's. */
export function typedReadings(form, prefill) {
  const p = prefill?.inputs || {};
  return READING_FIELDS.filter((f) => {
    if (f === 'townsend' && form.townsendNotKnown) return false;
    const v = form[f];
    if (v === '' || v === null || v === undefined) return false;
    return !sameValue(v, p[f]?.value);
  });
}

/** Whether a field still holds what the prefill gave it. */
export function isPrefilled(form, prefill, field) {
  const pre = prefill?.inputs?.[field];
  if (!pre || pre.source === null || pre.source === undefined) return false;
  // Nothing to keep: an unanswered family history, a reading never taken.
  if (pre.value === null || pre.value === undefined) return false;
  const v = form[field];
  if (typeof pre.value === 'boolean') return v === pre.value;
  if (typeof pre.value === 'string' && Number.isNaN(Number(pre.value)))
    return v === pre.value;
  return sameValue(v, pre.value) && v !== '';
}

/** Body mass index from the form's height and weight, to one decimal place. */
export function bmiOfForm(form) {
  const h = Number(form.heightCm);
  const w = Number(form.weightKg);
  if (!form.heightCm || !form.weightKg || !(h > 0) || !(w > 0)) return null;
  return Math.round((w / ((h / 100) * (h / 100))) * 10) / 10;
}

// The answers a saved draft can hold (services/qrisk3/draft.js DRAFT_FIELDS):
// everything but the readings, which a save records as clinical measurements,
// and the confirmation that QRISK3 applies, which is given at the calculation.
export const DRAFT_FIELDS = [
  'sex',
  'age',
  'ethnicity',
  'bmi',
  'systolicBpSd',
  'townsendNotKnown',
  'familyHistoryChd',
  'diabetes',
  ...CONDITION_KEYS
];

// A text input holds a string; a draft keeps numbers as numbers.
const TEXT_FIELDS = new Set(['age', 'ethnicity', 'bmi', 'systolicBpSd']);

/** The form as opened: the record's values, with the saved draft's answers over them. */
export function applyDraft(form, draft) {
  const overrides = draft?.overrides || {};
  const next = { ...form };
  for (const field of DRAFT_FIELDS) {
    const v = overrides[field];
    if (v === undefined || v === null) continue;
    next[field] = TEXT_FIELDS.has(field) ? text(v) : v;
  }
  return next;
}

/** Whether a field still holds what the saved draft gave it. */
export function isFromDraft(form, prefill, field) {
  const v = prefill?.draft?.overrides?.[field];
  if (v === undefined || v === null) return false;
  return TEXT_FIELDS.has(field) ? sameValue(form[field], v) : form[field] === v;
}

/**
 * What PUT /qrisk3/draft is sent: every answer as it stands, the readings one
 * by one (a height with no weight yet is still a reading), and never the
 * confirmation.
 */
export function draftPayloadOf(form) {
  const blankToUndefined = (v) => (v === '' ? undefined : v);
  const inputs = {
    sex: form.sex || undefined,
    age: blankToUndefined(form.age),
    ethnicity: blankToUndefined(form.ethnicity),
    heightCm: blankToUndefined(form.heightCm),
    weightKg: blankToUndefined(form.weightKg),
    bmi: bmiOfForm(form) === null ? blankToUndefined(form.bmi) : undefined,
    smoking: blankToUndefined(form.smoking),
    systolicBp: blankToUndefined(form.systolicBp),
    systolicBpSd: blankToUndefined(form.systolicBpSd),
    cholesterolHdlRatio: blankToUndefined(form.cholesterolHdlRatio),
    townsend: form.townsendNotKnown
      ? undefined
      : blankToUndefined(form.townsend),
    townsendNotKnown: form.townsendNotKnown === true,
    familyHistoryChd:
      typeof form.familyHistoryChd === 'boolean' ? form.familyHistoryChd : null,
    diabetes: form.diabetes || undefined,
    measuredOn: form.measuredOn
  };
  for (const key of CONDITION_KEYS) inputs[key] = form[key] === true;
  return inputs;
}

/** What POST /qrisk3/calculate is sent: the form, in the server's names. */
export function payloadOf(form) {
  const blankToUndefined = (v) => (v === '' ? undefined : v);
  const heightWeight = Boolean(form.heightCm && form.weightKg);
  const inputs = {
    sex: form.sex || undefined,
    age: blankToUndefined(form.age),
    ethnicity: blankToUndefined(form.ethnicity),
    heightCm: heightWeight ? form.heightCm : undefined,
    weightKg: heightWeight ? form.weightKg : undefined,
    bmi: heightWeight ? undefined : blankToUndefined(form.bmi),
    smoking: blankToUndefined(form.smoking),
    systolicBp: blankToUndefined(form.systolicBp),
    systolicBpSd: blankToUndefined(form.systolicBpSd),
    cholesterolHdlRatio: blankToUndefined(form.cholesterolHdlRatio),
    townsend: form.townsendNotKnown ? '' : blankToUndefined(form.townsend),
    townsendNotKnown: form.townsendNotKnown === true,
    familyHistoryChd:
      typeof form.familyHistoryChd === 'boolean' ? form.familyHistoryChd : null,
    diabetes: form.diabetes || undefined,
    confirmedNoCvdNoStatin: form.confirmedNoCvdNoStatin === true,
    measuredOn: form.measuredOn
  };
  for (const key of CONDITION_KEYS) {
    if (key === 'erectileDysfunction' && form.sex !== 'male') continue;
    inputs[key] = form[key] === true;
  }
  return inputs;
}
